import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useUpdateUserMutation } from "@/queries/auth"
import { userKeys } from "@/queries/user"
import { getAppLocale, setAppLocale } from "@/i18n"
import { type SupportedLocale } from "@/i18n/locales"

export function useLocale() {
  const { i18n, t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync: updateUser, isPending } = useUpdateUserMutation()

  const locale = getAppLocale()

  const changeLocale = useCallback(
    async (nextLocale: SupportedLocale, options?: { syncProfile?: boolean }) => {
      await setAppLocale(nextLocale)

      if (options?.syncProfile !== false) {
        try {
          await updateUser({ locale: nextLocale })
          await queryClient.invalidateQueries({ queryKey: userKeys.profile() })
        } catch {
          toast.error(t("errors.generic", { ns: "common" }))
        }
      }
    },
    [queryClient, t, updateUser],
  )

  return {
    locale,
    changeLocale,
    isChanging: isPending,
    i18n,
  }
}
